/**
 * レッスンの進捗（progress-store.md §7、lesson-format.md §8.2）。純粋関数。
 * 実験・書く課題は id で記録する（並べ替えで番号が変わってもずれない）。原稿から消えた id は数えない。
 */
import type { LocalDate } from './date';
import type { LessonProgress, LessonRecord } from './types';

/** 記録する出来事。open はレッスンを開いたとき。 */
export type LessonStep =
  { kind: 'open' } | { kind: 'experiment'; id: string } | { kind: 'write'; id: string };

/** 完了の判定に使う、いまの原稿の実験と書く課題の id（src/lib/lessons の lessonRequiredSteps）。 */
export interface LessonRequirement {
  experiments: readonly string[];
  writes: readonly string[];
}

export type LessonStatus = 'not-started' | 'in-progress' | 'completed';

export interface LessonState {
  status: LessonStatus;
  record: LessonRecord | null;
  /** 原稿にある id のうち済ませたもの。requirement を渡さないときは記録のまま。 */
  experimentsDone: string[];
  writesDone: string[];
}

function meets(rec: LessonRecord, req: LessonRequirement): boolean {
  return (
    req.experiments.every((id) => rec.experiments.includes(id)) &&
    req.writes.every((id) => rec.writes.includes(id))
  );
}

/**
 * 出来事を 1 つ当てる。初めてならレコードを作る（firstOpened = today）。
 * requirement を渡すと、全実験・全書く課題が済んだ時点で completed = today を付ける（一度だけ）。
 * 変化が無ければ同じ参照を返す。
 */
export function applyLessonStep(
  state: LessonProgress,
  lessonId: string,
  step: LessonStep,
  today: LocalDate,
  requirement?: LessonRequirement,
): LessonProgress {
  const prev = state.lessons[lessonId];
  let rec: LessonRecord = prev ?? { firstOpened: today, experiments: [], writes: [] };
  if (step.kind === 'experiment' && !rec.experiments.includes(step.id)) {
    rec = { ...rec, experiments: [...rec.experiments, step.id] };
  } else if (step.kind === 'write' && !rec.writes.includes(step.id)) {
    rec = { ...rec, writes: [...rec.writes, step.id] };
  }
  if (rec.completed === undefined && requirement && meets(rec, requirement)) {
    rec = { ...rec, completed: today };
  }
  if (rec === prev) return state;
  return { ...state, lessons: { ...state.lessons, [lessonId]: rec } };
}

/** レッスンの状態。requirement があれば消えた id を数えない。 */
export function lessonStatus(
  state: LessonProgress,
  lessonId: string,
  requirement?: LessonRequirement,
): LessonState {
  const record = state.lessons[lessonId] ?? null;
  if (record === null) {
    return { status: 'not-started', record: null, experimentsDone: [], writesDone: [] };
  }
  const experimentsDone = requirement
    ? requirement.experiments.filter((id) => record.experiments.includes(id))
    : [...record.experiments];
  const writesDone = requirement
    ? requirement.writes.filter((id) => record.writes.includes(id))
    : [...record.writes];
  const done =
    record.completed !== undefined || (requirement !== undefined && meets(record, requirement));
  return { status: done ? 'completed' : 'in-progress', record, experimentsDone, writesDone };
}

/** レッスンとカードの結び付き（lesson-format.md §8.1）。 */
export interface LessonCardLink {
  id: string;
  cards: readonly string[];
  memos: readonly string[];
}

/**
 * 今日のキューの新規に出さないカード（lesson-format.md §8.1 の推奨）。
 * どれかのレッスンの cards / memos に載り、載せたレッスンのどれも完了していないカード。
 * lessons には一覧に出すレッスンだけを渡す（draft のレッスンのカードを止めたままにしないため）。
 * 既に記録のあるカード（復習）は buildDailyQueue が止めない。
 */
export function heldNewCards(
  lessons: readonly LessonCardLink[],
  state: LessonProgress,
): Set<string> {
  const released = new Set<string>();
  const linked = new Set<string>();
  for (const l of lessons) {
    const done = state.lessons[l.id]?.completed !== undefined;
    for (const id of [...l.cards, ...l.memos]) {
      linked.add(id);
      if (done) released.add(id);
    }
  }
  return new Set([...linked].filter((id) => !released.has(id)));
}
